import { useCallback } from 'react';
import useAppDispatch from 'hooks/useAppDispatch';
import mapKeyToSnakeCase from 'utils/snakeCaseKeys';

import { track, trackPage } from './AnalyticsService';
import {
  TrackedDialogEventsKeysType,
  TrackedEventKeys,
  TrackedEventKeysType,
  TrackedGlobalEvents,
} from './TrackedEventKeys';

type TrackCheckboxClickedProps = {
  checkboxLabel: string;
  checkboxName: string;
  isChecked: boolean;
  [key: string]: unknown;
};

export type TrackButtonClickedProps = {
  buttonName: string;
  [key: string]: unknown;
};

export type TrackLinkClickedProps = {
  clickUrl: string;
  [key: string]: unknown;
};

export type TrackDialogViewedProps = {
  action: TrackedDialogEventsKeysType;
  dialogName: string;
  [key: string]: unknown;
};

type TrackBannerViewedProps = {
  bannerName: string;
  [key: string]: unknown;
};

export type TrackSearchPerformedProps = {
  searchTerm: string;
  [key: string]: unknown;
};

export type TrackThirdPartyAppStateChangedProps = {
  appName: string;
  stateChangeName: string;
  componentType: string;
  [key: string]: unknown;
};

export const useTrackEventsService = () => {
  const dispatch = useAppDispatch();

  const trackEmptyEvent = useCallback((event: TrackedEventKeysType) => dispatch(track(event, {})), [dispatch]);

  const trackEvent = useCallback(
    (event: TrackedEventKeysType, customMetadata: { [key: string]: unknown } = {}, useSalesAssisted?: boolean) => {
      const metadata = {
        pathname: window.location.pathname,
        ...mapKeyToSnakeCase(customMetadata),
      };
      dispatch(track(event, metadata, useSalesAssisted));
    },
    [dispatch]
  );

  const trackCheckboxClicked = useCallback(
    ({ checkboxLabel, checkboxName, isChecked, ...restCustomMetadata }: TrackCheckboxClickedProps) => {
      const metadata = {
        url: window.location.href,
        checkbox_value: checkboxLabel,
        checkbox_name: checkboxName,
        is_checked: isChecked,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };

      trackEvent(TrackedEventKeys.CheckboxClick, metadata);
    },
    [trackEvent]
  );

  const trackButtonClicked = useCallback(
    ({ buttonName, ...restCustomMetadata }: TrackButtonClickedProps, useSalesAssisted?: boolean) => {
      const metadata = {
        button_name: buttonName,
        url: window.location.href,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };

      trackEvent(TrackedEventKeys.Click, metadata, useSalesAssisted);
    },
    [trackEvent]
  );

  const trackDialogViewed = useCallback(
    ({ action, dialogName, ...restCustomMetadata }: TrackDialogViewedProps) => {
      const metadata = {
        action,
        dialog_name: dialogName,
        pathname: window.location.pathname,
        page_route: window.location.pathname,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };

      trackEvent(TrackedEventKeys.DialogViewed, metadata);
    },
    [trackEvent]
  );

  const trackPageViewed = useCallback(
    (page: string, customMetadata: { [key: string]: unknown } = {}, useSalesAssisted?: boolean) => {
      const metadata = {
        path: window.location.pathname,
        referrer: document.referrer,
        title: document.title,
        url: window.location.href,
        ...mapKeyToSnakeCase(customMetadata),
      };
      return dispatch(trackPage(page, metadata, useSalesAssisted));
    },
    [dispatch]
  );

  const trackLinkClicked = useCallback(
    ({ clickUrl, category, ...restCustomMetadata }: TrackLinkClickedProps) => {
      const metadata = {
        click_url: clickUrl,
        category: category || '',
        page_url: window.location.href,
        page_path: window.location.pathname,
        referrer: document.referrer,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };

      trackEvent(TrackedEventKeys.LinkClicked, metadata);
    },
    [trackEvent]
  );

  const trackSelectionSubmitted = useCallback(
    ({ buttonName, ...restCustomMetadata }: TrackButtonClickedProps) => {
      const metadata = {
        button_name: buttonName,
        url: window.location.href,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };

      trackEvent(TrackedEventKeys.SelectionSubmitted, metadata);
    },
    [trackEvent]
  );

  const trackBannerViewed = useCallback(
    ({ bannerName, ...restCustomMetadata }: TrackBannerViewedProps) => {
      const metadata = {
        banner_name: bannerName,
        url: window.location.href,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };
      trackEvent(TrackedEventKeys.BannerViewed, metadata);
    },
    [trackEvent]
  );

  const trackSearchPerformed = useCallback(
    ({ searchTerm, ...restCustomMetadata }: TrackSearchPerformedProps) => {
      const metadata = {
        search_term: searchTerm,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };
      trackEvent(TrackedGlobalEvents.SearchPerformed, metadata);
    },
    [trackEvent]
  );

  const trackThirdPartyAppStateChanged = useCallback(
    ({ appName, stateChangeName, componentType, ...restCustomMetadata }: TrackThirdPartyAppStateChangedProps) => {
      const metadata = {
        app_name: appName,
        state_change_name: stateChangeName,
        component_type: componentType,
        ...mapKeyToSnakeCase(restCustomMetadata || {}),
      };
      trackEvent(TrackedEventKeys.ThirdPartyAppStateChanged, metadata);
    },
    [trackEvent]
  );

  return {
    trackEmptyEvent,
    trackEvent,
    trackCheckboxClicked,
    trackButtonClicked,
    trackPageViewed,
    trackLinkClicked,
    trackSelectionSubmitted,
    trackDialogViewed,
    trackBannerViewed,
    trackSearchPerformed,
    trackThirdPartyAppStateChanged,
  };
};
